export const DEFAULT_API_REQUEST_TIMEOUT_MS = 30_000;

/**
 * Run a fetch with a bounded lifetime while preserving a caller-provided
 * AbortSignal. A hung request must settle so polling cannot accumulate an
 * unbounded number of in-flight calls.
 */
export const createTimedFetch = (
  fetchImpl: typeof fetch = fetch,
  timeoutMs: number = DEFAULT_API_REQUEST_TIMEOUT_MS
): typeof fetch => {
  return async (input: RequestInfo | URL, init: RequestInit = {}): Promise<Response> => {
    const controller = new AbortController();
    const upstreamSignal = init.signal;
    let timedOut = false;
    let timeoutHandle: ReturnType<typeof setTimeout> | undefined;

    const abortFromUpstream = () => controller.abort(upstreamSignal?.reason);
    if (upstreamSignal) {
      if (upstreamSignal.aborted) {
        abortFromUpstream();
      } else {
        upstreamSignal.addEventListener('abort', abortFromUpstream, { once: true });
      }
    }

    timeoutHandle = setTimeout(() => {
      timedOut = true;
      controller.abort(new Error(`Request timed out after ${timeoutMs}ms`));
    }, timeoutMs);

    try {
      return await fetchImpl(input, { ...init, signal: controller.signal });
    } catch (error) {
      if (timedOut) {
        const timeoutError = new Error(`Request timed out after ${timeoutMs}ms`);
        timeoutError.name = 'TimeoutError';
        throw timeoutError;
      }
      throw error;
    } finally {
      if (timeoutHandle !== undefined) {
        clearTimeout(timeoutHandle);
      }
      upstreamSignal?.removeEventListener('abort', abortFromUpstream);
    }
  };
};
